import type {SagaIterator} from 'redux-saga';
import {
    and,
    gte,
    lte,
    path,
    split,
    compose,
    uncurryN,
    pathEq,
    type,
    curry,
    equals,
    unfold,
    reduce,
    replace,
    toPairs,
    propOr,
} from 'ramda';
import FormData from 'form-data';

import type {ApplicationStoreType, MapType} from 'constants/flow/flowTypes';

import {select, take} from 'redux-saga/effects';
import type {OrderErrorType} from 'pages/checkout/store/reducers/orderData/orderDataType';
import Store from 'store/storages/cookieStorage';
import {MS_MEDIA_URL_PREFIX} from 'constants/api';
import type {ErrorReturnType} from '@typescript/common/authorizationTypes';

/**
 * Get path array from string
 * @param {string} - string path
 * @returns {Array<string>} - property path
 */
export const getPath = split('.');

/**
 * Get function which return prop value by path
 * @param {string} - property path
 * @returns (function(object)) - function which return propValue by passed object
 * @deprecated
 */
export const getProperty = compose(path, getPath);

/**
 * Is property equals to passed value
 * @param {string} - property path
 * @param {string} - value for condition
 * @return (function(object)) - function which return true/false by passed object
 */
export const isProperty = uncurryN(2, compose(pathEq, getPath));

/**
 * Is function
 * @param {*} - argument which type will be checked
 * @returns {boolean} - is function
 */
export const isFunction = compose(equals('Function'), type);

/**
 * Select status from store and wait for action which will fulfill it.
 *
 * @param {function(store: ApplicationStoreType): boolean} selector - Store selector.
 * @param {string} action - Wait for action.
 */
export function* waitForFulfilled(
    selector: (store: ApplicationStoreType) => boolean,
    action: string,
): SagaIterator {
    const result = yield select(selector);

    if (!result) yield take(action);
}

/**
 * Format template string by passed object.
 *
 * @param {string} template - Template string.
 * @param {MapType<string, any>} - Source object where key is name of replacement template {{key}} and value is what should be placed.
 * @returns {string} - Formatted string.
 *
 * @example
 * formatString(
 *     '{{murderer}} knocked out {{victim}} with {{weapon}}',
 *     {murderer: 'Favoreet', victim: 'Khvmkh', weapon: 'Pan'},
 * ); //=> 'Favoreet knocked out Khvmkh with Pan'
 */
export const formatString = curry((template: string, source: MapType<string, any>) =>
    reduce((acc, [key, value]) => replace(`{{${key}}}`, value, acc), template, toPairs(source)),
);

/**
 * Detects if a number in the range.
 * @param {string | number} value - The check value.
 * @param {Array<number>} rangeList - The list of numbers for checking.
 * @returns {boolean} - Flag is the value in the range.
 */
export function inRange(value: string | number, rangeList: Array<number>): boolean {
    return and(gte(value, rangeList[0]), lte(value, rangeList[1]));
}

/**
 * The iterator function. Receives one argument, `seed`
 * and returns either false to quit iteration or an array of length two to proceed.
 * For more details look at the https://ramdajs.com/docs/#unfold.
 * @param {number} boundary - The boundary value for stopping iteration.
 * @param {number} step - The step that the iterator will go through each iteration.
 * @returns {function(value: number): boolean | Array<number>} The iterator function.
 */
export function makeRangeIterator(
    boundary: number,
    step: number,
): (value: number) => boolean | Array<number> {
    return (value: number): boolean | Array<number> =>
        value > boundary ? false : [value, value + step];
}

/**
 * Returns a list of numbers from `from` (inclusive) to `to` (inclusive).
 * Uses the `unfold` function. For more details look at the https://ramdajs.com/docs/#unfold.
 * @param {number} from - The first number in the list.
 * @param {number} to - One more than the last number in the list.
 * @param {number} step - The step that the iterator will go through each iteration.
 * @returns {Array<number>} - List of numbers.
 */
export function range(from: number, to: number, step: number = 1): Array<number> {
    if (from === to) return [];
    return unfold(makeRangeIterator(to, step), from);
}

/**
 * Depending on value make a word plural or singular
 * @param {string} word for pluralising
 * @param {number} value for word
 * @returns {string} plural or singular form of a word
 */
export function pluralise(word: string, value: number): string {
    if (value === 1) return word;
    const trimmedWord = word.trim();
    if (
        ['s', 'x', 'z'].includes(trimmedWord.slice(-1)) ||
        ['sh', 'ch'].includes(trimmedWord.slice(-2))
    ) {
        return `${trimmedWord}es`;
    }
    return `${trimmedWord}s`;
}

/**
 * Extract error messages from rejected response from server
 * @param {{}} response - rejected response from server
 * @returns {Array<string>} error messages
 */
export function extractErrorMessagesFromResponse(response: {} = {}): Array<OrderErrorType> {
    const errors = propOr([], 'errors', propOr({}, 'data', response));
    const errorMessages = propOr([], 'error', propOr({}, 'messages', propOr({}, 'data', response)));
    return errors.length
        ? errors
        : errorMessages.map((error: {}): OrderErrorType => ({title: propOr('', 'message', error)}));
}

/**
 * Align passed string to camelCase notation
 * @param {string} string - passed string
 * @return {string} camelized string
 */
export function toCamelCase(string: string): string {
    return string.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
}

/**
 * Return is safari browser
 * @returns {boolean} - is safari
 */
export function isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
}

/**
 * Function for output error messages from response
 * @param {Array} errors - List of errors
 * @return {string} - list of messages
 */
export const getErrorMessages = (errors: ErrorReturnType[]): string => {
    const messageArray = errors.map(({message}) => message);
    return messageArray.join(' ');
};

/**
 * Serialize object to FromData
 * @param {{}} data - Object for serializing
 * @param {string} prefix - Prefix key
 * @param {FormData} [temp = new FormData] - Intermediate result of FormData
 * @return {FormData} - Result of FormData
 */
export const serializeToFormData = (
    data: {},
    prefix?: string,
    temp: FormData = new FormData(),
): FormData => {
    let result = temp;

    Object.keys(data).forEach((dataKey: string) => {
        const key = prefix ? `${prefix}[${dataKey}]` : dataKey;
        const value = data[dataKey];

        if (value !== null && typeof value === 'object') {
            if (value instanceof File || value instanceof Blob) {
                result.append(key, value);
            } else {
                result = serializeToFormData(value, key, result);
            }
        } else if (typeof value !== 'undefined') {
            result.append(key, value);
        }
    });

    return result;
};

/**
 * A function that returns an array of user location data using Akamai
 * @return {string[]} - location dataset
 */
export const getGeolocationByAkamai = (): string[] => (Store.read('x-akamai-geo') || '').split(',');

/**
 * Return string with upper first char
 * @param {string} value - source string
 * @return {string} - string with upper first char
 */
export const capitalize = (value: string): string => {
    return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Return the url to the media from MS
 * @param {string} url - part of the url
 * @return {string} - media url
 */
export const getMsMediaSourceUrl = (url: string): string =>
    url ? `${MS_MEDIA_URL_PREFIX}${url}` : '';
